import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, Trash2, GripVertical } from "lucide-react";
import { RichTextEditor } from "@/modules/hub/features/ResourceManagement/components/RichTextEditor/RichTextEditor";
import { FaqEditorProps } from "./FaqEditor.types";
import {
  FaqItem,
  FaqBlockContent,
} from "@/modules/hub/features/ResourceManagement/components/SortableBlock/types";

export function FaqEditor({ value, onChange }: FaqEditorProps) {
  const [items, setItems] = useState<FaqItem[]>([]);

  useEffect(() => {
    if (value) {
      try {
        const parsed = JSON.parse(value) as FaqBlockContent;
        if (parsed.items && Array.isArray(parsed.items)) {
          setItems(parsed.items);
        }
      } catch (e) {
        console.error("Failed to parse FAQ content:", e);
        setItems([]);
      }
    }
  }, [value]);

  const updateContent = (newItems: FaqItem[]) => {
    setItems(newItems);
    const content: FaqBlockContent = { items: newItems };
    onChange(JSON.stringify(content));
  };

  const addItem = () => {
    const newItem: FaqItem = {
      question: "",
      answer: "",
    };
    updateContent([...items, newItem]);
  };

  const updateItem = (index: number, field: keyof FaqItem, value: string) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    updateContent(newItems);
  };

  const removeItem = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    updateContent(newItems);
  };

  const moveItem = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= items.length) return;
    const newItems = [...items];
    const [movedItem] = newItems.splice(fromIndex, 1);
    newItems.splice(toIndex, 0, movedItem);
    updateContent(newItems);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-900 dark:text-white">
          FAQ Items
        </h4>
        <Button onClick={addItem} size="sm" type="button" variant="outline">
          <Plus className="h-4 w-4 mr-1" />
          Add Question
        </Button>
      </div>

      {items.length === 0 && (
        <div className="text-center py-8 text-muted-foreground border-2 border-dashed border-gray-200 dark:border-slate-700 rounded-lg">
          No FAQ items yet. Click "Add Question" to get started.
        </div>
      )}

      <div className="space-y-4">
        {items.map((item, index) => (
          <Card key={index}>
            <CardContent className="pt-6">
              <div className="flex items-start gap-2">
                <div className="flex flex-col gap-1 mt-2">
                  <Button
                    className="h-6 w-6 cursor-grab"
                    disabled={index === 0}
                    onClick={() => moveItem(index, index - 1)}
                    size="icon"
                    type="button"
                    variant="ghost"
                  >
                    <GripVertical className="h-4 w-4" />
                  </Button>
                  <Button
                    className="h-6 w-6 cursor-grab"
                    disabled={index === items.length - 1}
                    onClick={() => moveItem(index, index + 1)}
                    size="icon"
                    type="button"
                    variant="ghost"
                  >
                    <GripVertical className="h-4 w-4" />
                  </Button>
                </div>

                <div className="flex-1 space-y-4">
                  <div>
                    <Label htmlFor={`question-${index}`}>Question</Label>
                    <Input
                      id={`question-${index}`}
                      onChange={(e) =>
                        updateItem(index, "question", e.target.value)
                      }
                      placeholder="Enter question..."
                      type="text"
                      value={item.question}
                    />
                  </div>

                  <div>
                    <Label htmlFor={`answer-${index}`}>Answer</Label>
                    <div className="border border-gray-200 dark:border-slate-700 rounded-md p-2">
                      <RichTextEditor
                        id={`faq-answer-${index}`}
                        initialContent={item.answer}
                        onChange={(content) =>
                          updateItem(index, "answer", content)
                        }
                        onRemove={() => {}}
                      />
                    </div>
                  </div>
                </div>

                <Button
                  className="text-destructive hover:text-destructive"
                  onClick={() => removeItem(index)}
                  size="icon"
                  type="button"
                  variant="ghost"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
